import { useEffect, type ComponentType } from "react";
import { useCommissionStore } from "../stores/commissionStore";
import { MotionRoot, StepTransition } from "../motion/primitives";
import { StepIndicator } from "./StepIndicator";
import { ArtistHeader } from "./ArtistHeader";
import { StatusBanner } from "./StatusBanner";
import { WizardAside, MobileWizardBar } from "./WizardAside";
import { TypeStep } from "./steps/TypeStep";
import { SceneStep } from "./steps/SceneStep";
import { CharactersStep } from "./steps/CharactersStep";
import { ContactStep } from "./steps/ContactStep";
import { ReviewTermsStep } from "./steps/ReviewTermsStep";
import { useArtistOptions } from "../hooks/useArtistOptions";
import { computeChecklist } from "../lib/checklist";
import { STEP_IDS, type IntakeArtist, type StepId } from "../types";

const STEPS: Record<StepId, ComponentType<{ artist: IntakeArtist }>> = {
  type: TypeStep, scene: SceneStep, characters: CharactersStep, contact: ContactStep, review: ReviewTermsStep,
};

export function CommissionWizard({ artist }: { artist: IntakeArtist }) {
  const draft = useCommissionStore();
  const options = useArtistOptions(artist)!;
  useEffect(() => { useCommissionStore.getState().startForArtist(artist.slug); }, [artist.slug]);

  const items = computeChecklist(draft, { ratingOptions: options.ratingOptions });
  // A step is reachable once every required item on earlier steps is done.
  const reachable = (id: StepId) => {
    const idx = STEP_IDS.indexOf(id);
    return items.filter((i) => i.required && STEP_IDS.indexOf(i.step) < idx).every((i) => i.done);
  };
  const Step = STEPS[draft.step];

  return (
    <MotionRoot>
      <div className="relative min-h-screen bg-[var(--color-bg)] pb-28 lg:pb-12">
        <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
          <ArtistHeader artist={artist} />
          <StatusBanner artist={artist} />
          <StepIndicator reachable={reachable} />
          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            <main className="lg:col-span-2">
              <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 sm:p-8">
                <StepTransition stepKey={draft.step} direction={draft.direction}>
                  <Step artist={artist} />
                </StepTransition>
              </div>
            </main>
            <aside className="hidden lg:block">
              <div className="sticky top-24 space-y-4"><WizardAside artist={artist} /></div>
            </aside>
          </div>
        </div>
        <MobileWizardBar artist={artist} />
      </div>
    </MotionRoot>
  );
}
